"use client";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { debounce } from "@/ui/hooks/use-debounced-value";
import { Label } from "@/ui/shadcn/label";
import { Switch } from "@/ui/shadcn/switch";
import { StatefulColorPicker } from "@/ui/stateful-color-picker";
import { YnsLink } from "@/ui/yns-link";
import { Grid2X2Icon, LayoutPanelTopIcon } from "lucide-react";
import type { YnsComponentForm } from "../types";
import type { ProductDetails } from "./product-details";
import { ProductRelatedSelect } from "./product-related-select";

export const ProductDetailsForm: YnsComponentForm<typeof ProductDetails> = ({ data, theme, props }) => {
	const context = useStoreBuilderContext<typeof ProductDetails>();

	if (!props) {
		return null;
	}

	return (
		<div className="grid gap-4 relative pb-20">
			<p className="leading-snug text-sm text-neutral-600">
				Edit all product details on a dedicated page:{" "}
				<YnsLink href={`/admin/products/${props.slug}`} className="underline">
					Edit product
				</YnsLink>
			</p>

			<div>
				<Label className="flex items-center gap-2 mt-2 w-full">
					<Tabs
						defaultValue={data.imageLayout ?? "main"}
						className="w-full"
						onValueChange={(value) => {
							context.saveSelectedPageSectionField({
								fieldPath: "imageLayout",
								value,
							});
						}}
					>
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="main">
								<LayoutPanelTopIcon className="w-4 h-4 mr-1" />
								Main
							</TabsTrigger>
							<TabsTrigger value="grid">
								<Grid2X2Icon className="w-4 h-4 mr-1" />
								Grid
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</Label>
			</div>
			<div>
				<Label className="flex items-center gap-2 mt-2">
					<Switch
						required
						defaultChecked={data.showStickyBar ?? true}
						onCheckedChange={debounce(
							(value) => {
								context.saveSelectedPageSectionField({
									fieldPath: "showStickyBar",
									value,
								});
							},
							300,
							{ leading: true, trailing: true },
						)}
					/>
					<span>Show sticky bar</span>
				</Label>
				<p className="text-[0.8rem] mt-1 text-muted-foreground">
					The bar will be visible when the "Add to cart" button is not currently visible (i.e., when users
					scroll down)
				</p>
			</div>

			<div>
				<h3>Related Products</h3>
				<div className="flex flex-col space-y-4">
					<ProductRelatedSelect selectedProduct={data.relatedProducts?.[0] || ""} position={0} />
					<ProductRelatedSelect selectedProduct={data.relatedProducts?.[1] || ""} position={1} />
				</div>
			</div>

			<Label className="grid gap-2 w-max">
				<span>Font Color</span>
				<StatefulColorPicker
					defaultValue={theme?.color}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionThemeField({
								fieldPath: "color",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>

			<Label className="grid gap-2 w-max">
				<span>Price Color</span>
				<StatefulColorPicker
					defaultValue={theme?.priceColor}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionThemeField({
								fieldPath: "priceColor",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>

			<Label className="grid gap-2 w-max">
				<span>Button Background Color</span>
				<StatefulColorPicker
					defaultValue={theme?.buttonBackgroundColor}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionThemeField({
								fieldPath: "buttonBackgroundColor",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>

			<Label className="grid gap-2 w-max">
				<span>Button Text Color</span>
				<StatefulColorPicker
					defaultValue={theme?.buttonTextColor}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionThemeField({
								fieldPath: "buttonTextColor",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>

			<Label className="grid gap-2 w-max">
				<span>Button Background Color when mouse is over</span>
				<StatefulColorPicker
					defaultValue={theme?.buttonHoverBackgroundColor}
					onValueChange={debounce(
						(value) => {
							context.saveSelectedPageSectionThemeField({
								fieldPath: "buttonHoverBackgroundColor",
								value,
							});
						},
						300,
						{ trailing: true },
					)}
				/>
			</Label>
		</div>
	);
};
